import { z } from 'zod';
// ---------------------------------------------------------------------------
// Knowledge inbox (design §8) — the queue the reconciler/librarian drains to
// integrate new material into the topic wiki. Supports requeue-after-processing:
// re-adding a ref that already has a `done` row inserts a fresh `pending` row and
// marks the prior row `requeued`, so a topic is revisited rather than treated as
// finished and each pass keeps its own row (docs/m4-decisions.md §2).
//
// The kinds are the producer vocabulary verbatim (knowledge-librarian-runbook.md),
// not a collapsed set — how the reconciler obtains the source differs per kind, so
// naming them what the producers emit removes any mapping layer.
// ---------------------------------------------------------------------------
export const KNOWLEDGE_INBOX_KINDS = [
    'research_published',
    'theme_observation',
    'meeting_insight',
    'linear_doc',
    'finding',
    'answer',
    'decision',
    'manual',
];
export const KNOWLEDGE_INBOX_STATUSES = ['pending', 'processing', 'done', 'requeued'];
/** What an entry's `ref` points at — selects the reconciler's source reader. */
export const KNOWLEDGE_REF_TYPES = [
    'research_id',
    'finding_id',
    'theme_key',
    'granola_meeting',
    'linear_doc_url',
    'free_text',
];
export const KNOWLEDGE_INBOX_KIND_SPECS = {
    research_published: {
        refType: 'research_id',
        dereferenceable: true,
        summary: 'A research artifact was published; integrate its conclusions into the topic wiki.',
    },
    theme_observation: {
        refType: 'theme_key',
        dereferenceable: true,
        summary: 'A durable or recurring theme surfaced by a digest run.',
    },
    meeting_insight: {
        refType: 'granola_meeting',
        dereferenceable: false,
        summary: 'A durable meeting conclusion. The ref (granola:<meeting_id>) is provenance only — the note carries the already-distilled, privacy-checked source.',
    },
    linear_doc: {
        refType: 'linear_doc_url',
        dereferenceable: true,
        summary: 'A Linear canonical doc to distill (never copy) into the wiki.',
    },
    finding: {
        refType: 'finding_id',
        dereferenceable: true,
        summary: 'An assistant finding distilled from a conversation.',
    },
    answer: {
        refType: 'free_text',
        dereferenceable: false,
        summary: 'An ad-hoc session answer that required real synthesis worth keeping.',
    },
    decision: {
        refType: 'free_text',
        dereferenceable: false,
        summary: 'A decision-log entry with cross-project relevance.',
    },
    manual: {
        refType: 'free_text',
        dereferenceable: false,
        summary: 'A hand-queued pointer.',
    },
};
/**
 * Kinds whose ref names an Almanac artifact that carries a visibility, which must
 * be checked before the ref is queued or integrated: `private` items are never
 * wiki-ingested (migration 0006) and `almanac-wiki` has no private tier — an
 * owner-only item landing there is org-readable and permanent in git history.
 */
export function requiresVisibilityCheck(kind) {
    const { refType } = KNOWLEDGE_INBOX_KIND_SPECS[kind];
    return refType === 'research_id' || refType === 'finding_id';
}
export const KnowledgeInboxSchema = z
    .object({
    inbox_id: z.string().min(1),
    kind: z.enum(KNOWLEDGE_INBOX_KINDS),
    /** what to integrate: a research_id, a theme key, a Linear doc url, etc. */
    ref: z.string(),
    /** free-form instruction/context for the librarian pass */
    note: z.string().nullable(),
    status: z.enum(KNOWLEDGE_INBOX_STATUSES),
    requeue_count: z.number().int().min(0),
    created_at: z.string(),
    processed_at: z.string().nullable(),
})
    .strict();
/**
 * The `knowledge_add` write boundary. A kind whose ref cannot be dereferenced must
 * carry its source in `note` — otherwise the row is unresolvable by the time the
 * reconciler drains it, so reject it here instead of queueing a dead pointer.
 */
export const KnowledgeAddSchema = z
    .object({
    kind: z.enum(KNOWLEDGE_INBOX_KINDS),
    ref: z.string().trim().min(1),
    // Trim before the min-length check so a whitespace-only note cannot satisfy
    // the note-as-source requirement below.
    note: z.string().trim().min(1).optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (!KNOWLEDGE_INBOX_KIND_SPECS[value.kind].dereferenceable && !value.note) {
        ctx.addIssue({
            code: 'custom',
            path: ['note'],
            message: `kind "${value.kind}" has a ref that cannot be dereferenced, so \`note\` must carry the distilled source`,
        });
    }
});
